import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import qs from 'qs'
import * as Sentry from '@sentry/browser'
import { jwtTokenHandler } from './jwtTokenHandler'

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_HOUSEKEEPER_WEB_URL}/api`
})
instance.defaults.paramsSerializer = params => {
  return qs.stringify(params, { arrayFormat: 'repeat' })
}
instance.interceptors.request.use(
  async reqConfig => {
    if (Vue.prototype.$getCookie('comLoginToken')) {
      await jwtTokenHandler() // 確保 JWT Token 有效
      const xCorrelationId = Vue.prototype.$getGuid()
      reqConfig.headers = {
        authorization: Vue.prototype.$getCookie('JwtToken'),
        'x-requestid': Vue.prototype.$getGuid(),
        'X-Correlation-Id': xCorrelationId
      }
      Sentry.setTag('X-Correlation-Id', xCorrelationId)
      return reqConfig
    } else {
      // 若沒有 webCookie 且 API 需要登入進社區，則取消這筆請求
      if (!webCookie && newConfig.metadata.isApiNoNeedToLoginCommunity !== true) {
        const error = new Error('請求已取消：缺少認證 Cookie')
        error.code = 'CANCELLED_NO_COOKIE'
        return Promise.reject(error)
      }
    }
  },
  err => {
    return Promise.reject(err)
  }
)
instance.interceptors.response.use(
  res => res,
  err => {
    console.log(err.response)
    // eslint-disable-next-line eqeqeq
    if (err && err.response && err.response.status == 400) {
      return Promise.reject(err)
    // eslint-disable-next-line eqeqeq
    } else if (err && err.response && err.response.status == 401 && err.config.url !== '/Arrived/Total' && err.config.url !== '/IdCard') {
      Vue.prototype.$alert('登入過期或驗證失敗，請重新登入', '警告', {
        confirmButtonText: '確定',
        type: 'warning',
        showClose: false,
        roundButton: true,
        closeOnClickModal: false
      }).then(() => {
        // eslint-disable-next-line eqeqeq
        if (err.response.status == 401) { router.push({ name: 'LoginMain' }) } else if (err.response.status == 403) { Vue.prototype.$toMainPage() }
      })
      return Promise.reject(err)
    } else if (err && err.response && err.response.status === 409 || err.response.status === 404) {
      Vue.prototype.$alert(err.response.data.ErrorMessage.Description, '警告', {
        confirmButtonText: '確定',
        type: 'warning',
        showClose: false,
        roundButton: true,
        closeOnClickModal: false
      }).then(() => {
        // eslint-disable-next-line eqeqeq
        if (err.response.status == 404) { router.push({ name: 'LoginMain' }) }
      })
      return Promise.reject(err)
    } else {
      return Promise.reject(err)
    }
  }
)

// Swagger: https://housekeeper-web-dev.kingnetsmart.com.tw/swagger/index.html#/Guest
export const apiGuest = {
  // tab
  GuestRecordTabs: {
    GetRegisterFormUrl: (config) => instance.get('/v1/Guest/RegisterFormUrl', config), // 取得現場訪客登記表單QRCode
    GetComQRcodeEnable: (config) => instance.get('/v1/Guest/Features/QRcodeEnable', config) // 取得社區有無開啟QRCode功能
  },
  // 即將抵達訪客列表
  PlusGuestReservation: {
    GetGuestData: (queryPath, config) => instance.get(`/v1/Guest/${queryPath.id}`, config), // 取得訪客資料
    PatchEditGuestData: (queryPath, requestData, config) => instance.patch(`/v1/Guest/${queryPath.id}`, requestData, config), // 修改訪客資料
    PostCreateQRcode: (queryPath, requestData, config) => instance.post(`/v1/Guest/QRcode/${queryPath.id}`, requestData, config), // 取得門禁QRCode
    PostArrivedInfo: (queryPath, requestData, config) => instance.post(`/v1/Guest/${queryPath.id}/ArrivedInfo`, requestData, config), // 訪客抵達
    GetBookGuest: (requestData, config) => instance.get('/v1/Guest/BookGuest', Object.assign({}, { params: requestData }, config)), // 即將抵達訪客列表
    GetIdCard: (config) => instance.get('/v1/Guest/IdCard', config), // 取得社區押證清單
    PostCreateAndArrived: (requestData, config) => instance.post('/v2/Guest/CreateAndArrived', requestData, config), // 管理員現場登記
    PostCancelBooked: (queryPath, config) => instance.post(`/v1/Guest/${queryPath.id}/CancelBooked`, config), // 取消訪客預約
    PutEditBooked: (queryPath, requestData, config) => instance.put(`/v1/Guest/${queryPath.id}/EditBooked`, requestData, config)// 修改訪客登記
  },
  // 歷史訪客紀錄
  GuestRecord: {
    GetGuestRecordData: (requestData, config) => instance.get('/v1/Guest/Record', Object.assign({}, { params: requestData }, config)), // 取得歷史訪客紀錄
    PostLeaved: (queryPath, config) => instance.post(`/v1/Guest/${queryPath.id}/Leaved`, config), // 訪客離開
    GetGuestRecordExport: (requestData, config) => instance.get('/v1/Guest/Record/Export', Object.assign({}, { params: requestData }, config)), // 取得歷史訪客紀錄匯出檔案
    GetGuestRecordTotal: (requestData, config) => instance.get('/v1/Guest/Record/Total', Object.assign({}, { params: requestData }, config)) // 取得歷史訪客紀錄搜尋總筆數
  },
  // 設定
  SetUpDialog: {
    GetGuestCauseType: (config) => instance.get('/v1/Guest/CauseType', config), // 取得訪客設定列表
    PostGuestCauseType: (requestData, config) => instance.post('/v1/Guest/CauseType', requestData, config), // 新增訪客事由
    DeleteGuestCauseType: (queryPath, config) => instance.delete(`/v1/Guest/CauseType/${queryPath.id}`, config), // 刪除訪客事由
    PatchGuestCauseType: (queryPath, requestData, config) => instance.patch(`/v1/Guest/CauseType/${queryPath.id}`, requestData, config), // 修改訪客資料
    GetVendorType: (config) => instance.get('/v1/Guest/VendorTypes', config), // 取得廠商列表
    PostVendorType: (queryPath, requestData, config) => instance.post(`/v1/Guest/CauseType/${queryPath.causeTypeId}/VendorType`, requestData, config), // 新增廠商
    DeleteVendorType: (queryPath, config) => instance.delete(`/v1/Guest/CauseType/${queryPath.causeTypeId}/VendorType/${queryPath.id}`, config), // 刪除廠商
    PatchVendorType: (queryPath, requestData, config) => instance.patch(`/v1/Guest/CauseType/${queryPath.causeTypeId}/VendorType/${queryPath.id}`, requestData, config) // 修改廠商
  },
  // header （plus版）目前訪客
  GuestDialog: {
    GetGuestArrivedData: (requestData, config) => instance.get('/v1/Guest/Arrived', Object.assign({}, { params: requestData }, config)), // 取得目前訪客列表
    GetGuestArrivedTotal: (config) => instance.get('/v1/Guest/Arrived/Total', config) // 取得目前訪客總筆數
  }
}
